import { useEffect, useState } from 'react'

/**
 * Rough explanation of each ScreenSize
 * | Key | Index | Media query | Device category |
 * |-----|-------|-------------|-----------------|
 * | ScreenSize.XS  | 1 | (max-width: 640px) | Mobile in portrait |
 * | ScreenSize.SM  | 2 | (min-width: 640px) | Tablets in portrait/Mobile in landscape |
 * | ScreenSize.MD  | 3 | (min-width: 768px) | Tablets in landscape/Small laptops |
 * | ScreenSize.LG  | 4 | (min-width: 1024px) | Laptops |
 * | ScreenSize.XL  | 5 | (min-width: 1280px) | Large laptops/Desktops |
 * | ScreenSize.XXL  | 6 | (min-width: 1366px) | Desktops |
 */
export const ScreenSize = {
  XS: 1,
  SM: 2,
  MD: 3,
  LG: 4,
  XL: 5,
  XXL: 6
} as const

const breakpoints = [
  { max: 640, size: ScreenSize.XS },
  { max: 768, size: ScreenSize.SM },
  { max: 1024, size: ScreenSize.MD },
  { max: 1280, size: ScreenSize.LG },
  { max: 1366, size: ScreenSize.XL },
  { max: Infinity, size: ScreenSize.XXL }
] as const

const getScreenSize = (
  width: number
): (typeof ScreenSize)[keyof typeof ScreenSize] => {
  return (
    breakpoints.find((breakpoint) => width < breakpoint.max)?.size ??
    ScreenSize.XXL
  )
}

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: 0,
    height: 0,
    screenSize: ScreenSize.XXL as (typeof ScreenSize)[keyof typeof ScreenSize]
  })

  const handleSize = () => {
    const width = window.innerWidth
    const screenSize = getScreenSize(width)
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
      screenSize
    })
  }

  useEffect(() => {
    handleSize()

    window.addEventListener('resize', handleSize)

    return () => window.removeEventListener('resize', handleSize)
  }, [])

  return windowSize
}

export default useWindowSize
